import { ActivatedRoute, Router } from '@angular/router';
import {
  ChangeDetectorRef,
  Component,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ViewId, ViewPresetService } from '@services/view-preset.service';

import { AgBooleanCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-boolean-cell-renderer/ag-boolean-cell-renderer.component';
import { AgDataObjectCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-data-object-cell-renderer/ag-data-object-cell-renderer.component';
import { AgDateCellRendererComponent } from 'src/app/ag-grid/components/cell-renderers/ag-date-cell-renderer/ag-date-cell-renderer.component';
import { AgGridUserComponent } from '../../../ag-grid/components/cell-renderers/user/user.component';
import { AlertEditorComponent } from '../../components/alert-editor/alert-editor.component';
import { AppState } from 'src/app/reducers';
import { ColDef } from 'ag-grid-community';
import { ColGroupDef } from 'ag-grid-community';
import { CoreService } from '../../services/core.service';
import { GeneralDynamicAgGridComponent } from 'src/app/ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component';
import { LoggerService } from '@services/logger.service';
import { NomObjectLifecycleStatus } from '../../constants';
import { ObjectEditorService } from '../../services/object-editor.service';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import { redirectToObject } from 'src/app/shared/utils/object-utils';
import { AgDateRangeFilterComponent } from 'src/app/ag-grid/components/ag-date-range-filter/ag-date-range-filter.component';

@Component({
  selector: 'app-alerts',
  templateUrl:
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.html',
  styleUrls: [
    '../../../ag-grid/components/general-dynamic-ag-grid/general-dynamic-ag-grid.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlertsComponent extends GeneralDynamicAgGridComponent {
  @ViewChild('alertEditor') alertEditor: AlertEditorComponent;

  public viewId: ViewId = ViewId.Alerts;

  // @TODO WAP-435: Auto-loading of data objects is not yet implemented for in-line forms
  hasMasterDetail = false;

  protected tableTitle: string = this.translateService.instant(
    'alerts.title.alerts',
  );
  protected tableIcon: any = ['fas', 'triangle-exclamation'];

  protected generalGridClassName: string = 'alerts-grid';

  constructor(
    protected store: Store<AppState>,
    protected route: ActivatedRoute,
    protected cdr: ChangeDetectorRef,
    protected router: Router,
    protected logger: LoggerService,
    protected viewPresetService: ViewPresetService,
    protected toastService: ToastService,
    protected translateService: TranslateService,
    private coreService: CoreService,
    private editService: ObjectEditorService,
  ) {
    super(
      store,
      route,
      cdr,
      logger,
      router,
      viewPresetService,
      toastService,
      translateService,
    );
  }

  onDetailsContainerReady(): void {
    // Dynamically create the ExportSettingsComponent
    // Clear existing content in the details container
    this.dynamicDetailsContainer.clear();

    // Dynamically create the component
    const componentRef =
      this.dynamicDetailsContainer.createComponent(AlertEditorComponent);

    // Pass the selected object to the dynamically created component
    componentRef.instance.alert = this.selectedObject;
    componentRef.instance.alert.id = this.selectedObject.id;
    this.editor = componentRef.instance;
  }

  protected fetchAgGridData(request: any): Observable<any> {
    return this.coreService.fetchUserAlertsAgGrid(request);
  }

  createColumnDefs() {
    let columnDefs: (ColDef | ColGroupDef)[] = [
      {
        headerName: this.translateService.instant('grid.column.alert-company'),
        initialHide: true,
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'company.name',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 180,
            initialHide: true,
          },
          {
            headerName: this.translateService.instant('grid.column.is-enabled'),
            field: 'company.is_enabled',
            minWidth: 100,
            maxWidth: 120,
            initialHide: true,
            filterParams: {
              values: ['true', 'false'],
            },
            cellRenderer: AgBooleanCellRendererComponent,
            cellRendererParams: {
              isActive: 'data.company.is_enabled',
            },
          },
        ],
      },
      {
        headerName: this.translateService.instant('grid.column.alert-parent'),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.workspace'),
            field: 'workspace.title',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 180,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'workspace.title',
              icon: ['fas', 'folder'],
              objectId: 'workspace.id',
              objectClassId: 'workspace.class_id',
              lifecycleStatus: 'workspace.lifecycle_status',
              onClick: (data) => {
                if (data.can_view && data.can_edit) {
                  const url = this.router.serializeUrl(
                    this.router.createUrlTree([
                      environment.baseUrlEnvironmentSuffix,
                      'analytics',
                      'workspace',
                      data.workspace.id,
                    ]),
                  );
                  window.open(url, '_blank');
                } else {
                  this.toastService.showInfo(
                    this.translateService.instant(
                      'alerts.error.you-do-not-have-permissions-or-object-not-active',
                    ),
                    this.translateService.instant('alerts.error.not-available'),
                  );
                }
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            ...this.createNomenclatureColumnDef({
              headerNameKey: 'grid.column.alert-context-object-type',
              fieldKey: 'context_object.type_id',
              fieldValue: 'context_object.type_name',
              nomenclatureName: 'object_type',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            }),
            initialHide: true,
          },
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'feed.title',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 250,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'context_object.title',
              objectId: 'context_object.id',
              objectClassId: 'context_object.class_id',
              objectTypeId: 'context_object.type_id',
              lifecycleStatus: 'context_object.lifecycle_status',
              onClick: (data) => {
                if (
                  data.context_object.lifecycle_status_id ===
                    NomObjectLifecycleStatus.ACTIVE &&
                  data.can_view &&
                  data.can_edit
                ) {
                  const url = this.router.serializeUrl(
                    this.router.createUrlTree([
                      environment.baseUrlEnvironmentSuffix,
                      'analytics',
                      'workspace',
                      data.workspace.id,
                      'feed',
                      data.context_object.id,
                    ]),
                  );
                  window.open(url, '_blank');
                } else {
                  this.toastService.showInfo(
                    this.translateService.instant(
                      'alerts.error.you-do-not-have-permissions-or-object-not-active',
                    ),
                    this.translateService.instant('alerts.error.not-available'),
                  );
                }
              },
            },
            cellStyle: { overflow: 'visible' },
          },
        ],
      },
      {
        headerName: this.translateService.instant('grid.column.alert-details'),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.name'),
            field: 'title',
            filter: 'agTextColumnFilter',
            minWidth: 200,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'title',
              objectId: 'id',
              objectClassId: 'object_class_id',
              objectTypeId: 'type_id',
              lifecycleStatus: 'lifecycle_status.id',
              onClick: (data) => {
                if (
                  data?.lifecycle_status.id ===
                    NomObjectLifecycleStatus.ACTIVE &&
                  data.can_view &&
                  data.can_edit
                ) {
                  redirectToObject(data, this.router, true);
                } else {
                  this.toastService.showInfo(
                    this.translateService.instant(
                      'alerts.error.you-do-not-have-permissions-or-alert-not-active',
                    ),
                    this.translateService.instant('alerts.error.not-available'),
                  );
                }
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            headerName: this.translateService.instant(
              'grid.column.description',
            ),
            field: 'description',
            filter: 'agTextColumnFilter',
            initialHide: true,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.alert-trigger-type',
            ),
            field: 'trigger_type.title',
            filter: 'agTextColumnFilter',
            minWidth: 160,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.attached-dimension',
            ),
            field: 'dimension.title',
            filter: 'agTextColumnFilter',
            sortable: true,
            minWidth: 150,
          },
          {
            ...this.createNomenclatureColumnDef({
              headerNameKey: 'grid.column.alert-execution-status',
              fieldKey: 'execution_status.id',
              fieldValue: 'execution_status.title',
              nomenclatureName: 'alert_execution_status',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            }),
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-last-triggered',
            ),
            field: 'time_last_trigger.absolute',
            filter: AgDateRangeFilterComponent,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.recipients-count',
            ),
            field: 'recipients_count',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            sortable: true,
            maxWidth: 160,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.alert-are-you-recipient',
            ),
            field: 'is_user_subscribed',
            sortable: false,
            floatingFilter: false,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            maxWidth: 200,
            cellRenderer: (params) => (params.value ? 'Yes' : 'No'),
          },
          {
            headerName: this.translateService.instant(
              'grid.column.alert-user-status',
            ),
            field: 'user_status.title',
            sortable: false,
            floatingFilter: false,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,

            maxWidth: 150,
          },
          {
            ...this.createNomenclatureColumnDef({
              headerNameKey: 'grid.column.lifecycle-status',
              fieldKey: 'lifecycle_status.id',
              fieldValue: 'lifecycle_status.title',
              nomenclatureName: 'object_lifecycle_status',
              otherFilterParams: {
                defaultToNothingSelected: true,
              },
            }),
          },
        ],
      },
      {
        headerName: this.translateService.instant(
          'grid.column-group.audit-trail',
        ),
        marryChildren: true,
        children: [
          {
            headerName: this.translateService.instant('grid.column.owner'),
            field: 'owner',
            filter: 'agTextColumnFilter',
            maxWidth: 280,
            sortable: true,
            cellRenderer: AgDataObjectCellRendererComponent,
            cellRendererParams: {
              titleKey: 'owner.name',
              avatarKey: 'owner.avatar_url',
              objectId: 'owner.id',
              objectClassId: 'owner.object_class_id',
              lifecycleStatus: 'owner.lifecycle_status',
              onClick: (data) => {
                this.router.navigate(['admin', 'home', data.user.id]);
              },
            },
            cellStyle: { overflow: 'visible' },
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-created',
            ),
            field: 'time_created.absolute',
            filter: AgDateRangeFilterComponent,
            maxWidth: 180,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
          },
          {
            headerName: this.translateService.instant(
              'grid.column.time-updated',
            ),
            field: 'time_updated.absolute',
            filter: AgDateRangeFilterComponent,
            maxWidth: 180,
            sortable: true,
            cellRenderer: AgDateCellRendererComponent,
          },
        ],
      },
    ];

    return columnDefs;
  }
}
